import { useState } from 'react';

import { ia } from 'lib/helpers/utility';

import Button from 'components/shared/Button/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Icon from 'components/shared/Icon/Icon';
import { show as showIntercom } from 'components/shared/IntercomButton/lib/positionMessenger';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Spinner from 'components/shared/spinner/spinner';

const OrganizationCard = ({
  organizationsData,
  handleSubmit,
  practiceName,
  isFetchingNextPage
}) => {
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  const handleConfirm = () => {
    handleSubmit(selectedOrganization);
    setSelectedOrganization(null);
  };

  if (!ia(organizationsData) && !isFetchingNextPage) {
    return (
      <div className="flex w-full flex-col items-center justify-center px-6 py-12 text-center">
        <Icon icon="no-patient" />
        <h3 className="mt-4 text-lg font-600 text-neutral-800">No organization assigned</h3>
        <p className="mt-2 max-w-[520px] text-sm font-400 text-neutral-600">
          You are not currently assigned to an organization. If your practice is part of a group or
          network, our staff will link you automatically – no action needed. To add a location,
          contact support; each must have its own MID for compliance. If this seems incorrect, reach
          support.
        </p>
        <Button
          text="Contact support"
          icon="call-calling-mono"
          className="mt-6"
          variant="link"
          onClick={showIntercom}
        />
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {ia(organizationsData) &&
          organizationsData?.map((organization) => (
            <div
              key={organization?.id}
              className="flex flex-col gap-6 rounded-md border border-solid border-neutral-200 p-5 hover:border-neutral-300 hover:bg-neutral-25">
              <div className="flex gap-3">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md border border-solid border-[#E9EAEB] p-2">
                  <Icon icon="practice-mono" size={32} />
                </div>
                <div className="flex flex-col">
                  <h6 className="max-w-[230px] overflow-hidden text-ellipsis text-nowrap text-base font-600 text-neutral-800">
                    {organization?.name}
                  </h6>
                  <p className="max-w-[230px] overflow-hidden text-ellipsis text-nowrap text-sm font-400 text-neutral-800">
                    {organization?.locations[0]?.name}
                  </p>
                </div>
              </div>
              <div className="flex items-end justify-between gap-3">
                <div className="flex gap-3 sm:flex-col">
                  <div className="flex max-w-[50px] flex-col gap-1">
                    <label className="text-sm">ID</label>
                    <p className="text-nowrap text-sm font-500 text-neutral-800">
                      {organization?.id}
                    </p>
                  </div>
                  <div className="flex flex-1 flex-col gap-1">
                    <label className="text-sm">Owner</label>
                    <p className="max-w-[130px] overflow-hidden text-ellipsis text-nowrap text-sm font-500 text-neutral-800">
                      {organization?.owners?.[0]?.user?.fullName}
                    </p>
                  </div>
                </div>
                <Button
                  style="outlined"
                  text="Add to Organization"
                  className="text-nowrap border-[#E9EAEB] bg-transparent text-neutral-900 hover:border-neutral-200 hover:bg-neutral-50"
                  size="xs"
                  onClick={() => setSelectedOrganization(organization)}
                />
              </div>
            </div>
          ))}

        {isFetchingNextPage && (
          <div className="col-span-full flex items-center justify-center py-4">
            <Spinner />
          </div>
        )}
      </div>

      <Confirm
        isOpen={!!selectedOrganization}
        handleClose={() => setSelectedOrganization(null)}
        icon="info-circle-filled"
        title="Add to organization?"
        message={`Are you sure you want to add ${practiceName || 'this practice'} to ${selectedOrganization?.name}?`}
        footerActions={{
          primary: {
            text: 'Add to organization',
            onClick: handleConfirm
          }
        }}
      />
    </>
  );
};

export default withErrorBoundary(OrganizationCard);
